import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Navbar from '../NavBar';
import Footer from '../Footer';
import SectionBreak from '../SectionBreak';
import { useShop } from '../../context/ShopContext';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, subtotal: cartSubtotal, clearCart } = useShop();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    country: '',
    region: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('standard');
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [orderNumber] = useState(`#${Math.random().toString(36).substr(2, 9).toUpperCase()}`);
  
  const subtotal = cartSubtotal;
  const tax = subtotal * 0.08; 
  const shipping = 0.00;
  const total = subtotal + tax + shipping;
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setStep(2);
  };
  
  const handleShippingSubmit = (e) => {
    e.preventDefault();
   
    if (!shippingInfo.firstName || !shippingInfo.address || !shippingInfo.postalCode) {
      toast.error("Please fill out required shipping information");
      return;
    }
    setStep(3);
  };
  
  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      toast.error('Së pari shtoni produkte në shportë');
      return;
    }
    toast.success(`Order ${orderNumber} has been confirmed!`);
    setOrderConfirmed(true);
    setStep(4);
    clearCart();
  };

  const handleEditStep = (stepToEdit) => {
    if (orderConfirmed) {
      toast('You can edit your information even after confirmation', { icon: 'ℹ️' });
    }
    setStep(stepToEdit);
  };
  
  const renderStepIndicator = () => {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => handleEditStep(1)} 
            className={`flex items-center ${step >= 1 ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${step >= 1 ? 'bg-green-100 text-green-600' : 'bg-gray-200'}`}>
              {step > 1 ? '✓' : '1'}
            </div>
            <span className="ml-2 text-sm font-medium">Client Info</span>
          </button>
          
          <div className="flex-grow border-t border-gray-300 mx-4"></div>
          
          <button 
            onClick={() => step >= 2 && handleEditStep(2)} 
            className={`flex items-center ${step >= 2 ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${step >= 2 ? 'bg-green-100 text-green-600' : 'bg-gray-200'}`}>
              {step > 2 ? '✓' : '2'}
            </div>
            <span className="ml-2 text-sm font-medium">Shipping</span>
          </button>
          
          <div className="flex-grow border-t border-gray-300 mx-4"></div>
          
          <button 
            onClick={() => step >= 3 && handleEditStep(3)} 
            className={`flex items-center ${step >= 3 ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${step >= 3 ? 'bg-green-100 text-green-600' : 'bg-gray-200'}`}>
              {step > 3 ? '✓' : '3'}
            </div>
            <span className="ml-2 text-sm font-medium">Payment</span>
          </button>
        </div>
      </div>
    );
  };
  
  const renderOrderSummary = () => {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-bold text-center mb-4">ORDER SUMMARY</h2>
        
        {cartItems.length === 0 ? (
          <div className="text-center text-gray-500 py-6">
            Shporta është bosh.
          </div>
        ) : (
          cartItems.map(item => {
            const productCode = item.modelNumber || item.productCode || item.id;
            const sizeLabel = item.size || item.selectedSize || '—';
            return (
              <div key={item.id} className="flex mb-4 pb-4 border-b border-gray-100">
                <div className="w-20 h-20 flex-shrink-0">
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="w-full h-full object-cover rounded-md border border-gray-200"
                  />
                </div>
                <div className="ml-4">
                  <h3 className="font-medium">{item.name}</h3>
                  <p className="text-sm text-gray-500">Kodi: {productCode}</p>
                  <p className="text-sm text-gray-500">Madhësia: {sizeLabel}</p>
                  <p className="font-medium">${item.price.toFixed(2)}</p>
                </div>
              </div>
            );
          })
        )}
        
        <div className="space-y-2 mt-4">
          <div className="flex justify-between">
            <span className="text-gray-600">Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Shipping</span>
            <span>${shipping.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold pt-2 border-t border-gray-200">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        {orderConfirmed && (
          <div className="mt-4 p-3 bg-green-50 rounded-md">
            <p className="text-sm font-medium text-green-700">Order confirmed</p>
            <p className="text-xs text-green-600">{orderNumber}</p>
          </div>
        )}
      </div>
    );
  };
  
  const renderStep1 = () => {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-lg font-bold mb-4">1. Client Information</h2>
        <form onSubmit={handleEmailSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
            <input 
              id="email" 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button 
              type="button" 
              onClick={() => navigate('/cart')}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 w-full border border-input bg-background hover:bg-accent hover:text-accent-foreground"
            >
              Back to Cart
            </button>
            <button 
              type="submit" 
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 w-full bg-black text-white hover:bg-black/90"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    );
  };
  
  const renderStep2 = () => {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-4 bg-green-50 p-3 rounded">
            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</div>
            <div>
              <p className="text-sm font-medium">Email confirmed</p>
              <p className="text-xs text-gray-500">{email}</p>
            </div>
          </div>
        </div>
        
        <h2 className="text-lg font-bold mb-4">2. Shipping Details</h2>
        <form onSubmit={handleShippingSubmit}>
          <div className="mb-4">
            <div className="flex gap-4 mb-4">
              <div className="flex-1 grid gap-2">
                <div className="flex items-center space-x-2">
                  <input 
                    type="radio" 
                    value="standard" 
                    id="standard" 
                    name="shipping" 
                    checked={paymentMethod === 'standard'}
                    onChange={() => setPaymentMethod('standard')}
                    className="aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <label htmlFor="standard" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Standard Shipping</label>
                </div>
              </div>
              <div className="flex-1 grid gap-2">
                <div className="flex items-center space-x-2">
                  <input 
                    type="radio" 
                    value="pickup" 
                    id="pickup" 
                    name="shipping"
                    checked={paymentMethod === 'pickup'}
                    onChange={() => setPaymentMethod('pickup')}
                    className="aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <label htmlFor="pickup" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Store Pickup</label>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="firstName" className="block text-sm font-medium mb-1">First Name</label>
                <input 
                  id="firstName"
                  value={shippingInfo.firstName}
                  onChange={(e) => setShippingInfo({...shippingInfo, firstName: e.target.value})}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="lastName" className="block text-sm font-medium mb-1">Last Name</label>
                <input 
                  id="lastName"
                  value={shippingInfo.lastName}
                  onChange={(e) => setShippingInfo({...shippingInfo, lastName: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="address" className="block text-sm font-medium mb-1">Address</label>
              <input 
                id="address"
                value={shippingInfo.address}
                onChange={(e) => setShippingInfo({...shippingInfo, address: e.target.value})}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="city" className="block text-sm font-medium mb-1">City</label>
                <input 
                  id="city"
                  value={shippingInfo.city}
                  onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="region" className="block text-sm font-medium mb-1">Region</label>
                <input 
                  id="region"
                  value={shippingInfo.region}
                  onChange={(e) => setShippingInfo({...shippingInfo, region: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="postalCode" className="block text-sm font-medium mb-1">Postal Code</label>
                <input 
                  id="postalCode"
                  value={shippingInfo.postalCode}
                  onChange={(e) => setShippingInfo({...shippingInfo, postalCode: e.target.value})}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium mb-1">Phone</label>
                <input 
                  id="phone"
                  value={shippingInfo.phone}
                  onChange={(e) => setShippingInfo({...shippingInfo, phone: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              type="button" 
              onClick={() => handleEditStep(1)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 w-full border border-input bg-background hover:bg-accent hover:text-accent-foreground"
            >
              Back
            </button>
            <button 
              type="submit" 
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 w-full bg-black text-white hover:bg-black/90"
            >
              Continue to Payment
            </button>
          </div>
        </form>
      </div>
    );
  };
  
  const renderStep3 = () => {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-4 bg-green-50 p-3 rounded">
            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</div>
            <div>
              <p className="text-sm font-medium">Email confirmed</p>
              <p className="text-xs text-gray-500">{email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-4 bg-green-50 p-3 rounded">
            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</div>
            <div>
              <p className="text-sm font-medium">Shipping details confirmed</p>
              <p className="text-xs text-gray-500">{shippingInfo.address}, {shippingInfo.city}</p>
            </div>
          </div>
        </div>
        
        <h2 className="text-lg font-bold mb-4">3. Payment Method</h2>
        <form onSubmit={handlePaymentSubmit}>
          <div className="mb-4">
            <div className="flex gap-4 mb-4">
              <div className="flex-1 grid gap-2">
                <div className="flex items-center space-x-2">
                  <input 
                    type="radio" 
                    value="card" 
                    id="card" 
                    name="payment" 
                    checked={paymentMethod === 'card'}
                    onChange={() => setPaymentMethod('card')}
                    className="aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <label htmlFor="card" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Credit Card</label>
                </div>
              </div>
              <div className="flex-1 grid gap-2">
                <div className="flex items-center space-x-2">
                  <input 
                    type="radio" 
                    value="paypal" 
                    id="paypal" 
                    name="payment"
                    checked={paymentMethod === 'paypal'}
                    onChange={() => setPaymentMethod('paypal')}
                    className="aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <label htmlFor="paypal" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">PayPal</label>
                </div>
              </div>
            </div>
            
            {paymentMethod === 'card' && (
              <>
                <div className="mb-4">
                  <label htmlFor="cardNumber" className="block text-sm font-medium mb-1">Card Number</label>
                  <input 
                    id="cardNumber" 
                    placeholder="1234 5678 9012 3456" 
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label htmlFor="expiryDate" className="block text-sm font-medium mb-1">Expiry Date</label>
                    <input 
                      id="expiryDate" 
                      placeholder="MM/YY" 
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="cvv" className="block text-sm font-medium mb-1">CVV</label>
                    <input 
                      id="cvv" 
                      placeholder="123" 
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                    />
                  </div>
                </div>
              </>
            )}
            
            {paymentMethod === 'paypal' && (
              <div className="mb-4 p-4 bg-yellow-50 rounded-md border border-yellow-100">
                <p className="text-sm text-yellow-800">You will be redirected to PayPal to complete your payment</p>
              </div>
            )}
            
            <div className="flex items-center space-x-2 mb-4">
              <input 
                type="checkbox" 
                id="terms" 
                required
                className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
              />
              <label htmlFor="terms" className="text-sm text-gray-600">
                I accept the terms and conditions and privacy policy
              </label>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button 
              type="button" 
              onClick={() => handleEditStep(2)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 w-full border border-input bg-background hover:bg-accent hover:text-accent-foreground"
            >
              Back
            </button>
            <button 
              type="submit" 
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 w-full bg-black text-white hover:bg-black/90"
            >
              {orderConfirmed ? 'Update Payment' : 'Complete Order'}
            </button>
          </div>
        </form>
      </div>
    );
  };
  
  const renderStep4 = () => {
    return (
      <div className="bg-white p-10 rounded-lg border border-gray-200 text-center">
        <div className="flex flex-col items-center justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center text-green-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2">Porosia u konfirmua!</h2>
          <p className="text-gray-600 mb-4">{orderNumber}</p>
          <p className="text-sm text-gray-500 mb-6">
            Një email konfirmimi është dërguar {email}
          </p>
          
          <button 
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 w-full bg-black text-white hover:bg-black/90 mb-2" 
            onClick={() => navigate('/')}
          >
            Go to Home Page
          </button>
          <button 
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 w-full border border-input bg-background hover:bg-accent hover:text-accent-foreground" 
            onClick={() => handleEditStep(3)}
          >
            Edit Payment Details
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <>
    <Navbar />
    <SectionBreak />
    <div className="container mx-auto max-w-6xl px-4 py-8">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-red-600">Pagesa</h1>
        <p className="text-sm text-gray-500 mt-1">
          {orderConfirmed ? 'Your order has been confirmed' : 'Ktheni produktin për çfarëdo arsye brenda 7 ditëve për një rimbursim të plotë'}
        </p>
      </div>
      
      {renderStepIndicator()}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>
        
        <div>
          {renderOrderSummary()}
        </div>
      </div>
    </div>
    <SectionBreak />
    <Footer/>
    </>
  );
};

export default CheckoutPage;